import { useEffect, useRef, useState } from "preact/hooks";

import { Snackbar } from "../components/Snackbar";
import { useAnimals } from "../hooks/useAnimals";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { AnimalsView } from "./AnimalsView";

export interface AnimalsScreenProps {
  /** Admin only — see `AnimalsView`'s own docstring for the one admin-only
   * affordance this narrows (`AnimalDetailOverlay`'s "Add a note"). Every
   * authed role reaches this screen itself. */
  isAdmin: boolean;
  onBack: () => void;
}

/**
 * Sprint-008 A1 — the routed `/animals` screen. Lifted verbatim (markup,
 * classes, `aria-label`) out of `MoreView.tsx`'s former `subView ===
 * "animals"` branch so every existing e2e/vitest selector
 * (`.view[aria-label="Animals"]`, `.more-subheader`, etc.) keeps matching
 * unchanged — only the navigation wiring moved (`onBack` now calls
 * `navigate("/more")` instead of a local `setSubView("menu")`).
 *
 * Sprint-008 A2 — at `≥1024px` the sidebar links straight to `/animals`;
 * "‹ Back to More" makes no sense there (design-desktop-phase-a.md §0:
 * "there is no desktop 'More' nav item at all"), so the whole
 * `.more-subheader`/back-button wrapper is omitted at that breakpoint —
 * just the bare `<h1>`. Below `1024px` this is byte-for-byte what shipped
 * in A1 (same markup, same `onBack` wiring, same selectors).
 */
export function AnimalsScreen({ isAdmin, onBack }: AnimalsScreenProps) {
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const animals = useAnimals(true);
  const isDesktop = useIsDesktop();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useFocusHeadingOnMount(headingRef);

  // Same 5s auto-dismiss convention as every other local snackbar in this
  // app (design-spec-today-card.md §3.1).
  useEffect(() => {
    if (!snackbarMessage) return;
    const timer = setTimeout(() => setSnackbarMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [snackbarMessage]);

  return (
    <section class="view" aria-label="Animals">
      {isDesktop ? (
        <h1 ref={headingRef} tabIndex={-1} class="view-heading">
          Animals
        </h1>
      ) : (
        <div class="more-subheader">
          <button type="button" class="icon-btn" aria-label="Back to More" onClick={onBack}>
            ‹
          </button>
          <h1 ref={headingRef} tabIndex={-1} class="view-heading">
            Animals
          </h1>
        </div>
      )}
      <AnimalsView animals={animals} isAdmin={isAdmin} onNotify={setSnackbarMessage} />
      <Snackbar
        state={snackbarMessage ? { message: snackbarMessage } : null}
        onDismiss={() => setSnackbarMessage(null)}
      />
    </section>
  );
}
